import {
  CopyIcon,
  SearchIcon,
  X,
  Mail,
  Clock,
  HelpCircle,
  Link2,
} from 'lucide-react';
import {
  buildWorkflowPalettePayload,
  WORKFLOW_DND_MIME,
  type WorkflowPaletteItem,
} from 'constants/workflowPaletteDnD';
import { getNodeColor } from 'utils/workflowNodeHelpers';

interface RightSidePanelProps {
  addNewNode: (
    nodeType: string,
    config: { name: string; description: string; icon: string; color: string },
    sourceId?: string,
    targetId?: string
  ) => void;
  hasTriggerConditions?: boolean;
  isAttributeSegmentTrigger?: boolean;
}

const RightSidePanel = ({
  addNewNode,
  hasTriggerConditions = false,
  isAttributeSegmentTrigger = true,
}: RightSidePanelProps) => {
  const shouldRequireConditions =
    isAttributeSegmentTrigger && !hasTriggerConditions;
  const draggableItemBaseClass =
    'flex items-center space-x-3 p-2 rounded transition-all group';

  const handleDragStart = (
    event: React.DragEvent<HTMLDivElement>,
    item: WorkflowPaletteItem
  ) => {
    const payload = JSON.stringify(buildWorkflowPalettePayload(item));
    event.dataTransfer.setData(WORKFLOW_DND_MIME, payload);
    event.dataTransfer.effectAllowed = 'copyMove';
  };

  const buildNodeConfig = (
    name: string,
    color: string,
    description: string
  ) => ({
    name,
    description,
    icon:
      name === 'Email'
        ? '📧'
        : name === 'Time Delay'
          ? '⏱️'
          : name === 'True/False Branch'
            ? '❓'
            : name === 'Webhook'
              ? '🔗'
              : '💬',
    color,
  });

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border w-80 h-[calc(75vh-100px)] overflow-y-auto">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Build</h3>
          <button className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SearchIcon className="w-4 h-4 text-muted-foreground" />
          </div>
          <input
            type="text"
            placeholder="Search..."
            className="block w-full pl-10 pr-3 py-2 border border-border rounded-md leading-5 bg-card text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
          />
        </div>

        {/* Copy from workflow button */}
        <button className="w-full mb-4 flex items-center space-x-2 px-3 py-2 border border-border rounded text-sm text-foreground bg-muted hover:bg-muted/80">
          <CopyIcon className="w-4 h-4" />
          <span>Copy from workflow</span>
        </button>

        {/* Warning message only for attribute-segment trigger */}
        {shouldRequireConditions && (
          <div className="mb-4 p-3 bg-warning-background border border-warning/30 rounded-md">
            <p className="text-sm text-warning-foreground">
              ⚠️ Please configure trigger conditions first before adding
              workflow nodes.
            </p>
          </div>
        )}

        <div className=" h-[calc(60vh-200px)] overflow-y-auto">
          {/* Messages Section */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-foreground mb-3">
              Messages
            </h4>
            <div className="space-y-2">
              {[
                {
                  id: 'email',
                  name: 'Email',
                  icon: 'M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
                  color: getNodeColor('email'),
                },
              ].map((item, index) => (
                <div
                  key={index}
                  draggable={!shouldRequireConditions}
                  onDragStart={(event) =>
                    handleDragStart(event, {
                      nodeType: item.id,
                      config: buildNodeConfig(
                        item.name,
                        item.color,
                        `Send a ${item.name.toLowerCase()} message`
                      ),
                    })
                  }
                  className={`${draggableItemBaseClass} ${
                    !shouldRequireConditions
                      ? 'cursor-grab hover:bg-muted/50 active:cursor-grabbing'
                      : 'opacity-50 cursor-not-allowed bg-muted/50'
                  }`}
                  onClick={() => {
                    if (!shouldRequireConditions) {
                      const nodeConfig = buildNodeConfig(
                        item.name,
                        item.color,
                        `Send a ${item.name.toLowerCase()} message`
                      );
                      addNewNode(item.id, nodeConfig);
                    }
                  }}
                >
                  <div
                    className={`w-1 h-8 rounded-full ${!shouldRequireConditions ? 'bg-primary' : 'bg-muted-foreground'}`}
                  ></div>
                  <Mail
                    className={`w-5 h-5 ${!shouldRequireConditions ? 'text-foreground' : 'text-muted-foreground'}`}
                  />
                  <span
                    className={`text-sm ${!shouldRequireConditions ? 'text-foreground' : 'text-muted-foreground'}`}
                  >
                    {item.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Delays Section */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-foreground mb-3">Delays</h4>
            <div className="space-y-2">
              {[
                {
                  id: 'time-delay',
                  name: 'Time Delay',
                  icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
                  color: getNodeColor('time-delay'),
                },
              ].map((item, index) => (
                <div
                  key={index}
                  draggable={!shouldRequireConditions}
                  onDragStart={(event) =>
                    handleDragStart(event, {
                      nodeType: item.id,
                      config: buildNodeConfig(
                        item.name,
                        item.color,
                        'Wait for a specified time'
                      ),
                    })
                  }
                  className={`${draggableItemBaseClass} ${
                    !shouldRequireConditions
                      ? 'cursor-grab hover:bg-muted/50 active:cursor-grabbing'
                      : 'opacity-50 cursor-not-allowed bg-muted/50'
                  }`}
                  onClick={() => {
                    if (!shouldRequireConditions) {
                      const nodeConfig = buildNodeConfig(
                        item.name,
                        item.color,
                        'Wait for a specified time'
                      );
                      addNewNode(item.id, nodeConfig);
                    }
                  }}
                >
                  <div
                    className={`w-1 h-8 rounded-full ${!shouldRequireConditions ? 'bg-warning' : 'bg-muted-foreground'}`}
                  ></div>
                  <Clock
                    className={`w-5 h-5 ${!shouldRequireConditions ? 'text-foreground' : 'text-muted-foreground'}`}
                  />
                  <span
                    className={`text-sm ${!shouldRequireConditions ? 'text-foreground' : 'text-muted-foreground'}`}
                  >
                    {item.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Flow Control Section */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-foreground mb-3">
              Flow Control
            </h4>
            <div className="space-y-2">
              {[
                {
                  id: 'true-false',
                  name: 'True/False Branch',
                  icon: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
                  color: getNodeColor('true-false'),
                },
              ].map((item, index) => (
                <div
                  key={index}
                  draggable={hasTriggerConditions}
                  onDragStart={(event) =>
                    handleDragStart(event, {
                      nodeType: item.id,
                      config: buildNodeConfig(
                        item.name,
                        item.color,
                        'Branch based on a condition'
                      ),
                    })
                  }
                  className={`${draggableItemBaseClass} ${
                    hasTriggerConditions
                      ? 'cursor-grab hover:bg-muted/50 active:cursor-grabbing'
                      : 'opacity-50 cursor-not-allowed bg-muted/50'
                  }`}
                  onClick={() => {
                    if (hasTriggerConditions) {
                      const nodeConfig = buildNodeConfig(
                        item.name,
                        item.color,
                        'Branch based on a condition'
                      );

                      addNewNode(item.id, nodeConfig);
                    }
                  }}
                >
                  <div
                    className={`w-1 h-8 rounded-full ${hasTriggerConditions ? 'bg-info' : 'bg-muted-foreground'}`}
                  ></div>
                  <HelpCircle
                    className={`w-5 h-5 ${hasTriggerConditions ? 'text-foreground' : 'text-muted-foreground'}`}
                  />
                  <span
                    className={`text-sm ${hasTriggerConditions ? 'text-foreground' : 'text-muted-foreground'}`}
                  >
                    {item.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Integrations Section */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-foreground mb-3">
              Integrations
            </h4>
            <div className="space-y-2">
              {[
                {
                  id: 'webhook',
                  name: 'Webhook',
                  color: getNodeColor('webhook'),
                },
              ].map((item, index) => (
                <div
                  key={index}
                  draggable={!shouldRequireConditions}
                  onDragStart={(event) =>
                    handleDragStart(event, {
                      nodeType: item.id,
                      config: buildNodeConfig(
                        item.name,
                        item.color,
                        'Call an external webhook endpoint'
                      ),
                    })
                  }
                  className={`${draggableItemBaseClass} ${
                    !shouldRequireConditions
                      ? 'cursor-grab hover:bg-muted/50 active:cursor-grabbing'
                      : 'opacity-50 cursor-not-allowed bg-muted/50'
                  }`}
                  onClick={() => {
                    if (!shouldRequireConditions) {
                      const nodeConfig = buildNodeConfig(
                        item.name,
                        item.color,
                        'Call an external webhook endpoint'
                      );
                      addNewNode(item.id, nodeConfig);
                    }
                  }}
                >
                  <div
                    className={`w-1 h-8 rounded-full ${!shouldRequireConditions ? 'bg-info' : 'bg-muted-foreground'}`}
                  ></div>
                  <Link2
                    className={`w-5 h-5 ${!shouldRequireConditions ? 'text-foreground' : 'text-muted-foreground'}`}
                  />
                  <span
                    className={`text-sm ${!shouldRequireConditions ? 'text-foreground' : 'text-muted-foreground'}`}
                  >
                    {item.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RightSidePanel;
